// The IRC layout's logic (#154) without a browser: nick colors, the nick list's groups, window states for the
// switchbar, the `[HH:MM]` tag, the status lines and the `[image: …]` labels. scripts/irc-cases.mjs checks it.
// Part of the IRC layout: deleting components/irc/ removes it.
import { BLOOM_ID, BLOOM_NAME, exactAgeShort, relativeAgeShort, type Attachment, type TimestampFormat } from "@bloom/shared";

// ---- Nick colors ----

/**
 * The bright mIRC colors that read on a black pane: each has at least 4.5:1 contrast on #000000 (the cases check
 * it). A nick's color is a hash into this list, so a person has the same color in the pane and in the nick list.
 */
export const NICK_COLORS: readonly string[] = ["#ffff00", "#00ffff", "#fc7f00", "#00fc00", "#ff00ff", "#d2d2d2", "#ff5555", "#7f7fff"];

/**
 * The avatar's hash (`colorFor` in ui.tsx, `Theme.avatarIndex` on iOS): h = h × 31 + each UTF-16 unit, unsigned 32-bit.
 * The same eight slots, so a person has one nick color on the web, the phone and the Mac.
 */
export function nickIndex(key: string): number {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return h % NICK_COLORS.length;
}

export function nickColor(key: string): string {
  return NICK_COLORS[nickIndex(key)]!;
}

// ---- Times ----

/**
 * The tag before a line: "[18:20]". It follows Settings → Timestamps: the default shows the 24-hour time; Relative
 * and Exact show their short forms ("[3m]", "[0d 00:03]"). "off" shows none (there is no such setting yet; the
 * pure rule is here so a later toggle needs no new logic).
 */
export function timeTag(ts: number, now: number, format: TimestampFormat | "off"): string {
  if (format === "off") return "";
  if (format === "relative") return `[${relativeAgeShort(now - ts)}]`;
  if (format === "exact") return `[${exactAgeShort(now - ts)}]`;
  const d = new Date(ts);
  return `[${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}]`;
}

// ---- Lines ----

/** The channel's modes in its header. Every Bloom channel is +nt: no outside messages, topic set by operators. */
export const CHANNEL_MODES = "[+nt]";

/** "<maya>": a nick in angle brackets. */
export const nickTag = (name: string) => `<${name}>`;

/**
 * The status lines at the start of loaded history (green, each starting "* "). A channel says where you are and its
 * topic; a conversation says who with.
 */
export function startLines(c: { kind: "text" | "dm"; name: string; topic: string }, dmName: string): string[] {
  if (c.kind === "dm") return [`* Now talking with ${dmName}`];
  const out = [`* Now talking in #${c.name}`];
  const topic = c.topic.trim();
  if (topic) out.push(`* Topic is '${topic}'`);
  return out;
}

/** The line shown when the day changes, as mIRC does: "* Day changed to Sunday, October 25". */
export const dayLine = (label: string) => `* Day changed to ${label}`;

/** "/me ships it" is an action: the text after it, or null for any other message. */
export function actionText(body: string): string | null {
  const m = /^\/me(?:\s+([\s\S]*))?$/.exec(body);
  if (!m) return null;
  return (m[1] ?? "").trim();
}

const SIZE_UNITS = ["B", "KB", "MB", "GB"];
function bytes(n: number): string {
  let v = n;
  let i = 0;
  while (v >= 1024 && i < SIZE_UNITS.length - 1) {
    v /= 1024;
    i++;
  }
  return `${i === 0 || v >= 10 ? Math.round(v) : v.toFixed(1)} ${SIZE_UNITS[i]}`;
}

/** The link that stands for an attachment: "[image: IMG_4729.jpeg 1206×2347]", "[file: notes.pdf 12 KB]". */
export function attachmentLabel(a: Attachment): string {
  if (a.kind === "sticker") return `[sticker: ${a.name}]`;
  if (a.kind === "gif") return `[gif: ${a.title || "GIF"} ${a.width}×${a.height}]`;
  const size = a.width && a.height ? ` ${a.width}×${a.height}` : "";
  if (/^image\//.test(a.type)) return `[image: ${a.name}${size}]`;
  if (/^video\//.test(a.type)) return `[video: ${a.name}${size}]`;
  return `[file: ${a.name} ${bytes(a.size)}]`;
}

/** "[3 replies]", "[1 reply]". */
export const threadLabel = (count: number) => `[${count} ${count === 1 ? "reply" : "replies"}]`;

// ---- The nick list ----

export interface NickEntry {
  id: string;
  name: string;
  online: boolean;
  /** "owner" and "admin" are ops. */
  role: "owner" | "admin" | "member";
  bot?: boolean;
}

export interface NickGroup {
  /** "@" (owner and admins), "+" (online), "" (everyone else). */
  mark: "@" | "+" | "";
  label: string;
  nicks: NickEntry[];
}

const byName = (a: NickEntry, b: NickEntry) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }) || a.id.localeCompare(b.id);

/**
 * Who is in the channel, as IRC groups them: ops (@) are the owner and admins, whether or not they are online;
 * voice (+) is everyone else who is online (bots are always here); the rest are offline. Each group is alphabetical.
 * Empty groups are left out.
 */
export function groupNicks(entries: NickEntry[]): NickGroup[] {
  const ops = entries.filter((n) => n.role !== "member");
  const voice = entries.filter((n) => n.role === "member" && (n.online || n.bot));
  const rest = entries.filter((n) => n.role === "member" && !n.online && !n.bot);
  const groups: NickGroup[] = [
    { mark: "@", label: "OPS (owner, admins)", nicks: ops.sort(byName) },
    { mark: "+", label: "VOICE (online)", nicks: voice.sort(byName) },
    { mark: "", label: "AWAY", nicks: rest.sort(byName) },
  ];
  return groups.filter((g) => g.nicks.length > 0);
}

/** "@Frank", "+Evan", "bigmistert": a nick with its group's mark. */
export const nickWithMark = (mark: NickGroup["mark"], name: string) => `${mark}${name}`;

// ---- Windows (the switchbar and the phone's window tree) ----

/** How a window looks: the open one, one with a mention (bold), one with unread messages (red), or quiet. */
export type WindowState = "active" | "mention" | "unread" | "idle";

export function windowState(w: { active: boolean; unread: boolean; mentions: number; muted: boolean }): WindowState {
  if (w.active) return "active";
  if (w.mentions > 0) return "mention";
  if (w.unread && !w.muted) return "unread";
  return "idle";
}

/** The note beside a window in the phone's tree: "12 new", "@ you", or "". Mentions win. */
export function windowNote(w: { unreadCount: number; mentions: number; muted: boolean; active: boolean }): string {
  if (w.active) return "here";
  if (w.mentions > 0) return w.mentions > 1 ? `@ you (${w.mentions})` : "@ you";
  if (w.unreadCount > 0 && !w.muted) return `${w.unreadCount > 99 ? "99+" : w.unreadCount} new`;
  return "";
}

/** The tree's branches: "├─" for every window but the last, "└─" for the last. */
export const branch = (index: number, count: number) => (index === count - 1 ? "└─" : "├─");

// ---- Who is in the list ----

/**
 * The entries of the nick list: the server's people (or, in a DM, the people in it), @Bloom, and the server's bots.
 * `only` limits the people to those ids (a DM's members). Bots and @Bloom count as online.
 */
export function nickEntries(
  members: { id: string; name: string; role: NickEntry["role"] }[],
  online: Set<string>,
  bots: { id: string; name: string; disabled: boolean }[],
  only?: string[],
): NickEntry[] {
  const people = members.filter((m) => !only || only.includes(m.id)).map((m) => ({ id: m.id, name: m.name, online: online.has(m.id), role: m.role }));
  if (only) return people;
  return [
    ...people,
    { id: BLOOM_ID, name: BLOOM_NAME, online: true, role: "member" as const, bot: true },
    ...bots.filter((b) => !b.disabled).map((b) => ({ id: b.id, name: b.name, online: true, role: "member" as const, bot: true })),
  ];
}
